/**
 * Company Authentication Hook
 * Provides company authentication state and operations
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../../services/firebase/firebase';
import { 
  loginCompany, 
  registerCompany, 
  getCompanyProfile, 
  updateCompanyProfile,
  getCompanyStats 
} from '../services/companyAuthService';

/**
 * Company Authentication Hook
 * 
 * @returns {Object} Company auth state and operations
 */
const useCompanyAuth = () => {
  const navigate = useNavigate();
  
  // State
  const [currentUser, setCurrentUser] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    pendingApplications: 0
  });

  // Derived state
  const isAuthenticated = !!currentUser;
  const isVerified = companyProfile?.verified || false;
  const isComplete = !!(companyProfile?.companyName && companyProfile?.email);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Set loading state
   */
  const setLoadingState = useCallback((isLoading) => {
    setLoading(isLoading);
  }, []);

  /**
   * Initialize authentication state
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setLoading(true);
        setError(null);
        
        if (user) {
          // Get company profile
          const profile = await getCompanyProfile(user.uid);
          
          if (profile && profile.accountType === 'company') {
            setCurrentUser(user);
            setCompanyProfile(profile);
            
            // Load company stats
            try {
              const companyStats = await getCompanyStats(user.uid);
              setStats(companyStats);
            } catch (statsError) {
              console.warn('Could not load company stats:', statsError);
              // Don't fail authentication for stats error
            }
          } else {
            // User is not a company account
            setCurrentUser(null);
            setCompanyProfile(null);
            setStats({
              totalJobs: 0,
              activeJobs: 0,
              totalApplications: 0,
              pendingApplications: 0
            });
            
            if (user && !profile) {
              setError('Company profile not found. Please register as a company.');
            } else if (profile && profile.accountType !== 'company') {
              setError('This account is not registered as a company. Please use the student portal.');
            }
          }
        } else {
          setCurrentUser(null);
          setCompanyProfile(null);
          setStats({
            totalJobs: 0,
            activeJobs: 0,
            totalApplications: 0,
            pendingApplications: 0
          });
        }
      } catch (authError) {
        console.error('Error in auth state change:', authError);
        setError(authError.message || 'Authentication error occurred');
        setCurrentUser(null);
        setCompanyProfile(null);
      } finally {
        setLoading(false);
        setInitializing(false);
      }
    });

    return unsubscribe;
  }, []);

  /**
   * Login company user
   * 
   * @param {string} email - Company email
   * @param {string} password - Company password
   * @returns {Promise<Object>} Login result
   */
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await loginCompany(email, password);
      
      // Store token
      const token = await result.user.getIdToken();
      localStorage.setItem('companyAuthToken', token);
      
      // State will be updated by onAuthStateChanged
      return result;
    } catch (loginError) {
      console.error('Login error:', loginError);
      
      // Set user-friendly error messages
      let errorMessage = 'Login failed. Please try again.';
      
      if (loginError.code === 'auth/user-not-found' || loginError.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (loginError.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (loginError.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (loginError.message === 'Company profile not found') {
        errorMessage = 'This account is not registered as a company. Please register or use the student portal.';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Register new company
   * 
   * @param {Object} companyData - Company registration data
   * @returns {Promise<Object>} Registration result
   */
  const register = useCallback(async (companyData) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await registerCompany(companyData);
      
      // Store token
      const token = await result.user.getIdToken();
      localStorage.setItem('companyAuthToken', token);
      
      // State will be updated by onAuthStateChanged
      return result;
    } catch (registrationError) {
      console.error('Registration error:', registrationError);
      
      // Set user-friendly error messages
      let errorMessage = 'Registration failed. Please try again.';
      
      if (registrationError.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists';
      } else if (registrationError.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (registrationError.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use at least 6 characters';
      } else if (registrationError.code === 'auth/operation-not-allowed') {
        errorMessage = 'Company registration is currently disabled. Please contact support.';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout company user
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      await signOut(auth);
      
      // Clear local storage
      localStorage.removeItem('companyAuthToken');
      
      // Clear state
      setCurrentUser(null);
      setCompanyProfile(null);
      setStats({
        totalJobs: 0,
        activeJobs: 0,
        totalApplications: 0,
        pendingApplications: 0
      });
      
      // Navigate to company auth page
      navigate('/company/auth');
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
      setError('Failed to logout. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /**
   * Refresh company profile
   */
  const refreshProfile = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const profile = await getCompanyProfile(currentUser.uid);
      setCompanyProfile(profile);
      
      // Also refresh stats
      const companyStats = await getCompanyStats(currentUser.uid);
      setStats(companyStats);
    } catch (refreshError) {
      console.error('Error refreshing profile:', refreshError);
      setError('Failed to refresh profile data');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Update company profile
   * 
   * @param {Object} updates - Profile updates
   * @returns {Promise<void>}
   */
  const updateProfile = useCallback(async (updates) => {
    if (!currentUser) {
      throw new Error('Not authenticated');
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await updateCompanyProfile(currentUser.uid, updates);
      
      // Update local state
      setCompanyProfile(prev => ({
        ...prev,
        ...updates,
        updatedAt: new Date()
      }));
    } catch (updateError) {
      console.error('Error updating profile:', updateError);
      setError('Failed to update profile');
      throw updateError;
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Refresh company statistics
   */
  const refreshStats = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const companyStats = await getCompanyStats(currentUser.uid);
      setStats(companyStats);
    } catch (statsError) {
      console.warn('Could not refresh company stats:', statsError);
      // Don't throw error for stats refresh failure
    }
  }, [currentUser]);

  /**
   * Check if user has required permissions
   * 
   * @param {string} permission - Permission to check
   * @returns {boolean} Has permission
   */
  const hasPermission = useCallback((permission) => {
    if (!isAuthenticated || !companyProfile) return false;
    
    switch (permission) {
      case 'post_jobs':
        return isVerified;
      case 'manage_applications':
        return isAuthenticated;
      case 'view_analytics':
        return isAuthenticated;
      case 'manage_profile':
        return isAuthenticated;
      default:
        return false;
    }
  }, [isAuthenticated, isVerified, companyProfile]);

  /**
   * Get user display name
   */
  const getDisplayName = useCallback(() => {
    return companyProfile?.companyName || companyProfile?.contactPerson || 'Company User';
  }, [companyProfile]);

  /**
   * Get verification status message
   */
  const getVerificationStatus = useCallback(() => {
    if (!companyProfile) return { status: 'unknown', message: 'Profile not loaded' };
    
    if (companyProfile.verified) {
      return { status: 'verified', message: 'Your company is verified' };
    } else {
      return { 
        status: 'pending', 
        message: 'Your company account is under review. Verification is required to post jobs.' 
      };
    }
  }, [companyProfile]);

  /**
   * Check if profile is complete
   */
  const isProfileComplete = useCallback(() => {
    if (!companyProfile) return false;
    
    const requiredFields = [
      'companyName',
      'email',
      'contactPerson',
      'industry',
      'companySize',
      'location',
      'description'
    ];
    
    return requiredFields.every(field => 
      companyProfile[field] && companyProfile[field].toString().trim().length > 0
    );
  }, [companyProfile]);

  /**
   * Get missing profile fields
   */
  const getMissingProfileFields = useCallback(() => {
    if (!companyProfile) return [];
    
    const fieldLabels = {
      companyName: 'Company Name',
      email: 'Email',
      contactPerson: 'Contact Person',
      industry: 'Industry',
      companySize: 'Company Size',
      location: 'Location',
      description: 'Company Description'
    };
    
    const requiredFields = Object.keys(fieldLabels);
    
    return requiredFields
      .filter(field => 
        !companyProfile[field] || companyProfile[field].toString().trim().length === 0
      )
      .map(field => fieldLabels[field]);
  }, [companyProfile]);

  return {
    // State
    currentUser,
    companyProfile,
    loading,
    initializing,
    error,
    stats,
    
    // Derived state
    isAuthenticated,
    isVerified,
    isComplete,
    
    // Actions
    login,
    register,
    logout,
    refreshProfile,
    updateProfile,
    refreshStats,
    clearError,
    setLoadingState,
    
    // Utilities
    hasPermission,
    getDisplayName,
    getVerificationStatus,
    isProfileComplete,
    getMissingProfileFields
  };
};

export default useCompanyAuth;